// Crafting balance constants. Edit data, not module logic (plan §4).

import type { Quality } from '../tiers/index.js';

export type StationKind = 'refiner' | 'weaponsmith' | 'armorer';

export interface CraftingRecipe {
  id: string;
  station: StationKind;
  outputDefId: string;
  outputCount: number;
  outputTier: number;
  ingredients: ReadonlyArray<{ defId: string; count: number }>;
  craftingFame: number;
  craftMs: number;
}

// Quality probability table at base mastery level 0 — must sum to 1.
// Mastery shifts probability mass upward; see rollQuality.
export const BASE_QUALITY_WEIGHTS: ReadonlyArray<[Quality, number]> = [
  ['Normal', 0.6],
  ['Good', 0.25],
  ['Outstanding', 0.1],
  ['Excellent', 0.04],
  ['Masterpiece', 0.01],
];

// Per mastery level, shift up to 1% from Normal toward higher qualities.
export const MASTERY_QUALITY_SHIFT_PER_LEVEL = 0.005;

export const RECIPES: CraftingRecipe[] = [
  // ---- Refiner: raw → refined (T2) ----
  {
    id: 'refine.planks.t2',
    station: 'refiner',
    outputDefId: 'planks.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [{ defId: 'rawWood.t2', count: 2 }],
    craftingFame: 6,
    craftMs: 700,
  },
  {
    id: 'refine.ironBar.t2',
    station: 'refiner',
    outputDefId: 'ironBar.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [{ defId: 'rawOre.t2', count: 2 }],
    craftingFame: 6,
    craftMs: 700,
  },
  {
    id: 'refine.cloth.t2',
    station: 'refiner',
    outputDefId: 'cloth.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [{ defId: 'rawFiber.t2', count: 2 }],
    craftingFame: 6,
    craftMs: 700,
  },

  // ---- Weaponsmith ----
  {
    id: 'craft.sword.t2',
    station: 'weaponsmith',
    outputDefId: 'sword.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'planks.t2', count: 4 },
      { defId: 'ironBar.t2', count: 4 },
      { defId: 'wolfFang', count: 2 },
    ],
    craftingFame: 22,
    craftMs: 1500,
  },
  {
    id: 'craft.bow.t2',
    station: 'weaponsmith',
    outputDefId: 'bow.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'planks.t2', count: 6 },
      { defId: 'cloth.t2', count: 2 },
    ],
    craftingFame: 22,
    craftMs: 1500,
  },
  {
    id: 'craft.fireStaff.t2',
    station: 'weaponsmith',
    outputDefId: 'fireStaff.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'planks.t2', count: 4 },
      { defId: 'cloth.t2', count: 4 },
      { defId: 'wolfFang', count: 1 },
    ],
    craftingFame: 22,
    craftMs: 1500,
  },

  // ---- Armorer ----
  {
    id: 'craft.plate.chest.t2',
    station: 'armorer',
    outputDefId: 'plate.chest.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'ironBar.t2', count: 6 },
      { defId: 'cloth.t2', count: 1 },
    ],
    craftingFame: 24,
    craftMs: 1600,
  },
  {
    id: 'craft.plate.helm.t2',
    station: 'armorer',
    outputDefId: 'plate.helm.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'ironBar.t2', count: 3 },
      { defId: 'cloth.t2', count: 1 },
    ],
    craftingFame: 18,
    craftMs: 1300,
  },
  {
    id: 'craft.plate.shoes.t2',
    station: 'armorer',
    outputDefId: 'plate.shoes.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'ironBar.t2', count: 3 },
      { defId: 'cloth.t2', count: 1 },
    ],
    craftingFame: 18,
    craftMs: 1300,
  },
  {
    id: 'craft.leather.chest.t2',
    station: 'armorer',
    outputDefId: 'leather.chest.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'cloth.t2', count: 4 },
      { defId: 'wolfPelt', count: 4 },
    ],
    craftingFame: 22,
    craftMs: 1500,
  },
  {
    id: 'craft.leather.helm.t2',
    station: 'armorer',
    outputDefId: 'leather.helm.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [
      { defId: 'cloth.t2', count: 2 },
      { defId: 'wolfPelt', count: 2 },
    ],
    craftingFame: 16,
    craftMs: 1200,
  },
  {
    id: 'craft.cloth.chest.t2',
    station: 'armorer',
    outputDefId: 'cloth.chest.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [{ defId: 'cloth.t2', count: 6 }],
    craftingFame: 22,
    craftMs: 1500,
  },
  {
    id: 'craft.cloth.helm.t2',
    station: 'armorer',
    outputDefId: 'cloth.helm.t2',
    outputCount: 1,
    outputTier: 2,
    ingredients: [{ defId: 'cloth.t2', count: 3 }],
    craftingFame: 16,
    craftMs: 1200,
  },
];
